import { Component, Input, forwardRef, signal, ViewChild, OnChanges, SimpleChanges, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { FileInputComponent } from '../file-upload.component';
import { StripeCardComponent } from '../stripe-card/stripe-card.component';
import { ConsentType, StripeCredentials, StripeConsentPayload, StripeConsent, ConsentCategory, SmartConsentValue } from '@models/consent.model';
import { Stripe, StripeElements, StripePaymentElement, loadStripe } from '@stripe/stripe-js';
import { ConsentService } from '@services/consent.service';
import { UtilService } from '@services/util.service';
import { LoginService } from '@services/login.service';
import { LogService } from '@services/log.service';
import { MeterDetails } from '@models/meter.model';
import { formatDateForApi } from '@utils/meter.utils';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-smart-consent-options',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  MatProgressBarModule,
  MatButtonToggleModule,
  MatRadioModule,
  MatButtonModule,
    FileInputComponent,
    StripeCardComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmartConsentOptionsComponent),
      multi: true
    }
  ],
  templateUrl: './smart-consent-options.component.html',
  styleUrls: ['./smart-consent-options.component.scss']
})
export class SmartConsentOptionsComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() supportedSmartMeterConsentTypes: ConsentType[] = [
    ConsentType.MAC_ADDRESS,
    ConsentType.BILL_UPLOAD,
    ConsentType.STRIPE
  ];
  // selectedMethod removed: method derived from incoming form control value or defaults
  @Input() selectedMeters: MeterDetails[] = [];
  @Input() moveInDate: Date | null = null;
  @Input() termsAccepted = false;
  @Input() useRadioButtons = false; // New input to control display mode

  @ViewChild('billUpload') billUploadComponent!: FileInputComponent;

  consentMethod = signal<ConsentType>(ConsentType.STRIPE);
  macGuideExpanded = signal(false);
  isStripeCardValid = signal(false);

  // Stripe state managed here
  private consentService = inject(ConsentService);
  private utilService = inject(UtilService);
  private loginService = inject(LoginService);
  private logService = inject(LogService);
  stripe: Stripe | null = null;
  stripeElements: StripeElements | null = null;
  stripeCredentialsSignal = signal<StripeCredentials | null>(null);
  loadingStripeCredentialsSignal = signal(false);
  stripeInitializationFailedSignal = signal(false);

  macAddressControl = new FormControl<string>('', [
    Validators.required,
    Validators.pattern(/^[A-Za-z0-9]{4}$/)
  ]);

  private _onChange: (value: SmartConsentValue | null) => void = () => {};
  private _onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['supportedSmartMeterConsentTypes']) {
      // If current method becomes unsupported pick a new default
      if (!this.isMethodSupported(this.consentMethod())) {
        const def = this.getDefaultConsentType();
        this.setMethod(def, false);
      }
    }
  }

  ngOnInit(): void {
    // Ensure initial method is supported
    if (!this.isMethodSupported(this.consentMethod())) {
      this.setMethod(this.getDefaultConsentType(), false);
    }
    // If starting on BILL_UPLOAD ensure file subscription ready
    if (this.consentMethod() === ConsentType.BILL_UPLOAD) setTimeout(()=>this.setupBillUploadSubscription(),0);
    if (this.consentMethod() === ConsentType.STRIPE) setTimeout(()=>this.initializeStripe(),0);
  }
  private isMethodSupported(method: ConsentType): boolean {
    return (method === ConsentType.MAC_ADDRESS && this.isMacAddressSupported()) ||
           (method === ConsentType.BILL_UPLOAD && this.isBillUploadSupported()) ||
           (method === ConsentType.STRIPE && this.isStripeSupported());
  }


  // Expose helpers
  isMacAddressSupported(): boolean {
    return this.supportedSmartMeterConsentTypes.includes(ConsentType.MAC_ADDRESS);
  }
  isBillUploadSupported(): boolean {
    return this.supportedSmartMeterConsentTypes.includes(ConsentType.BILL_UPLOAD);
  }
  isStripeSupported(): boolean {
    return this.supportedSmartMeterConsentTypes.includes(ConsentType.STRIPE);
  }

  // MAC
  onMacChanged(): void {
    if (this.consentMethod() !== ConsentType.MAC_ADDRESS) return;
    if (this.macAddressControl.valid && this.macAddressControl.value) {
      const macValue = this.macAddressControl.value.toUpperCase();
      this.logService.addConsentActionLog('MAC_ADDRESS_VALID', { macAddress: macValue });
      this._onChange({ consentCategory: ConsentCategory.SMART_METER, consentType: ConsentType.MAC_ADDRESS, consentData: macValue });
      this._onTouched();
    } else {
      this._onChange(null);
    }
  }

  // Bill
  private setupBillUploadSubscription(): void {
    if (this.billUploadComponent) {
      this.billUploadComponent.registerOnChange((file: File | null) => {
        if (file) {
          this.logService.addConsentActionLog('BILL_UPLOADED', { fileName: file.name, fileSize: file.size });
        }
        this._onChange(file ? { consentCategory: ConsentCategory.SMART_METER, consentType: ConsentType.BILL_UPLOAD, consentData: file } : null);
        this._onTouched();
      });
    }
  }

  // Stripe
  onStripeCardChange(paymentElement: StripePaymentElement | null): void {
    const wasValid = this.isStripeCardValid();
    this.isStripeCardValid.set(!!paymentElement);
    
    // Log when stripe card becomes valid
    if (!wasValid && !!paymentElement) {
      this.logService.addConsentActionLog('STRIPE_CARD_VALID');
    }
    
  if (this.consentMethod() === ConsentType.STRIPE) {


    const creds = this.stripeCredentialsSignal();
    if (paymentElement && creds) {
        this._onChange({ consentCategory: ConsentCategory.SMART_METER, consentType: ConsentType.STRIPE, consentData: { stripe: this.stripe, stripeElements: this.stripeElements, stripePaymentElement: paymentElement, stripeCredentials: creds, } });
        this._onTouched();
      } else {
        this._onChange(null);
      }
    }
  }

  // CVA
  writeValue(value: SmartConsentValue | null): void {
    if (!value) {
      this.macAddressControl.reset('', { emitEvent: false });
      return;
    }
    // Sync method from incoming value
    if (value.consentType !== this.consentMethod()) {
      this.setMethod(value.consentType, false); // don't emit change yet
    }
    if (value.consentType === ConsentType.MAC_ADDRESS) {
      const raw = value.consentData;
      this.macAddressControl.setValue(typeof raw === 'string' ? raw : '', { emitEvent: false });
    }
  }
  registerOnChange(fn: (value: SmartConsentValue | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.macAddressControl.disable(); else this.macAddressControl.enable();
  }

  // Helpers
  propertyAddress(): string {
    return this.selectedMeters && this.selectedMeters.length > 0 ? (this.selectedMeters[0].addressAsLine || '') : '';
  }
  getDefaultConsentType(): ConsentType {
    const types = this.supportedSmartMeterConsentTypes || [];
    if (types.includes(ConsentType.MAC_ADDRESS)) return ConsentType.MAC_ADDRESS;
    if (types.includes(ConsentType.STRIPE)) return ConsentType.STRIPE;
    if (types.includes(ConsentType.BILL_UPLOAD)) return ConsentType.BILL_UPLOAD;
    return ConsentType.MAC_ADDRESS;
  }

  onMethodChange(method: ConsentType): void {
    if (method === this.consentMethod()) return;
    if (!this.isMethodSupported(method)) return;
    
    const previousMethod = this.consentMethod();
    this.logService.addConsentActionLog('CONSENT_METHOD_SWITCH', { 
      previousMethod, 
      newMethod: method 
    });
    
    this.setMethod(method, true);
  }

  private setMethod(method: ConsentType, emit: boolean): void {
    this.consentMethod.set(method);
    this.isStripeCardValid.set(false);
    this.macAddressControl.reset('', { emitEvent: false });
    if (emit) {
      this._onChange(null);
      this._onTouched();
    }
    if (method === ConsentType.BILL_UPLOAD) setTimeout(()=>this.setupBillUploadSubscription(),0);
    if (method === ConsentType.STRIPE) this.initializeStripe();
  }

  async initializeStripe(): Promise<void> {
    if (!this.isStripeSupported()) return;
    if (this.loadingStripeCredentialsSignal()) return;
    this.loadingStripeCredentialsSignal.set(true);
    this.stripeInitializationFailedSignal.set(false);
    try {
      const payload: StripeConsentPayload = {
        consentType: 'STRIPE',
        meterNo: this.selectedMeters[0]?.meterNo,
        moveInDate: formatDateForApi(this.moveInDate || new Date()),
        email: this.loginService.loginUser?.email || '',
        postCode: this.selectedMeters[0]?.postCode || '',
        city: this.selectedMeters[0]?.postTown || '',
        country: 'UK',
        addressLine: this.selectedMeters[0]?.addressAsLine || '',
        termAndConditionAccepted: this.termsAccepted ? 'yes' : 'no'
      };
      const credentials = await this.consentService.getStripeCredentials(payload).toPromise();
      if (!credentials) throw new Error('Failed to get Stripe credentials');
      this.stripeCredentialsSignal.set(credentials);
      this.stripe = await loadStripe(credentials.publishable_key);
      if (!this.stripe) throw new Error('Failed to initialize Stripe');
      this.stripeElements = this.stripe.elements({ clientSecret: credentials.stripeSecret, appearance: { theme: 'stripe' } });
    } catch (e) {
      console.error('Stripe init error', e);
      this.stripeInitializationFailedSignal.set(true);
      this.utilService.openSnackBar('Failed to initialize payment form', 'Error', 'error');
    } finally {
      this.loadingStripeCredentialsSignal.set(false);
    }
  }

  resetStripe(): void {
    this.isStripeCardValid.set(false);
    this.stripeCredentialsSignal.set(null);
    this.stripe = null;
    this.stripeElements = null;
    this.stripeInitializationFailedSignal.set(false);
    this.initializeStripe();
  }

  // buildConsentValue removed; explicit objects created at source for clarity
}
